import {
  Component,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
} from '@angular/core';

import {
  countHelpMatches,
  groupHelpResults,
  HelpSearchGroup,
  HelpSearchResult,
  MAX_HELP_SEARCH_RESULTS,
  searchHelp,
} from '../info/help-search';

/**
 * The Help Center search box and its results, shared by the Help Center landing
 * page and the info sidenav so the two stay in step.
 */
@Component({
  selector: 'ciao-help-search',
  templateUrl: './help-search.component.html',
  styleUrls: ['./help-search.component.less'],
  standalone: false,
})
export class HelpSearchComponent {
  /** Distinguishes the two placements: 'sidenav' (default) or 'page'. */
  @Input() layout: 'sidenav' | 'page' = 'sidenav';
  @Input() placeholder = 'Search help';
  /** Unique id so the label matches its own input when both are on a page. */
  @Input() inputId = 'help-search-input';

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  searchQuery = '';
  searchResults: HelpSearchResult[] = [];
  /** The same results, bucketed into their sections for display. */
  searchGroups: HelpSearchGroup[] = [];
  /** How many entries match in total, ignoring the result cap. */
  totalMatches = 0;
  /** Set by "view all entries" — drops the cap for the current query. */
  showingAll = false;

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  get isSearching(): boolean {
    return this.searchQuery.trim().length > 0;
  }

  /**
   * In the sidenav the results pop open over the page, so a click anywhere
   * outside should close them. The landing page renders its results inline,
   * where dismissing on an unrelated click would just lose someone's search.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.layout === 'page' || !this.isSearching) {
      return;
    }
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.clearSearch();
    }
  }

  /** Keyboard equivalent of clicking away. */
  @HostListener('keydown.escape')
  onEscape(): void {
    if (this.isSearching) {
      this.clearSearch();
    }
  }

  /** True while results are being held back by the cap. */
  get hasMoreMatches(): boolean {
    return !this.showingAll && this.totalMatches > this.searchResults.length;
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
    // A new query starts capped again.
    this.showingAll = false;
    this.totalMatches = countHelpMatches(query);
    this.runSearch();
  }

  /** Drop the cap and show every match for the current query. */
  viewAllEntries(): void {
    this.showingAll = true;
    this.runSearch();
  }

  private runSearch(): void {
    this.searchResults = searchHelp(
      this.searchQuery,
      this.showingAll ? Infinity : MAX_HELP_SEARCH_RESULTS
    );
    this.searchGroups = groupHelpResults(this.searchResults);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.searchResults = [];
    this.searchGroups = [];
    this.totalMatches = 0;
    this.showingAll = false;
    if (this.searchInput) {
      this.searchInput.nativeElement.value = '';
    }
  }
}
